import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WatchlistData } from '~/stores/use-chat/assistant-response.type';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';

import { mapData, MappedData } from './mapData';
import { PartialMessage } from './partial-message/PartialMessage';
import { WatchlistEntityMatchesTable } from './watchlist-entity-matches-table/WatchlistEntityMatchesTable';
import * as S from './WatchlistMessage.styles';

export interface WatchlistProps {
  data: WatchlistData;
}

export const WatchlistMessage = ({ data }: WatchlistProps) => {
  const { t } = useTranslation('intel');
  const status = data.status;
  const action = data.action;

  const [mappedData, setMappedData] = useState<MappedData>();
  const { getLists } = useWatchlist(state => ({ getLists: state.getLists }));

  useEffect(() => {
    const getTickers = async () => {
      const mapped = await mapData(data);
      setMappedData(mapped);
    };

    getTickers();
  }, [data]);

  useEffect(() => {
    const watchlistManipulated = status === 'success' && 'get-list' !== action;
    const watchlistPartiallyManipulated =
      status === 'partial' && 'get-list' !== action && data?.info?.id;

    if (watchlistManipulated || watchlistPartiallyManipulated) {
      getLists();
    }
  }, [status, action, data, getLists]);

  if (!mappedData) {
    return null;
  }

  const { entities, name, count, entitiesTags } = mappedData;

  if (status === 'fail') {
    return (
      <S.ReplyMessage data-testid="failed">
        {
          /* i18next-extract-disable-next-line */
          t(`intel:watchlist.failed.${action}`, {
            name,
            entities: entities || t('intel:watchlist.entities'),
          })
        }
      </S.ReplyMessage>
    );
  }

  if (status === 'partial') {
    return <PartialMessage data={data} name={name} action={action} />;
  }

  return (
    <>
      <S.ReplyMessage data-testid="success">
        {
          /* i18next-extract-disable-next-line */
          t(`intel:watchlist.success.${action}`, {
            name,
            new_name: data.info?.new_name,
            entities,
            count,
          })
        }
      </S.ReplyMessage>

      {!!entitiesTags.length && data?.info.id && (
        <WatchlistEntityMatchesTable
          tags={entitiesTags}
          watchlistId={data.info.id}
          name={name}
        />
      )}
    </>
  );
};
